import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useAtomValue, useSetAtom } from "jotai/react";
import { useForm } from "react-hook-form";
import { toast } from "sonner-native";

import { citizenshipStatusEnumSchema } from "@libs/api/patients";
import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { ApplicationYesNoButton } from "@/blocks/ApplicationYesNoButton";
import { InfoCard } from "@/blocks/InfoCard";
import { VnBox } from "@/components/VnBox";
import { VnCard, VnCardTitle } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnRadioGroupInput } from "@/components/VnRadioGroupInput/VnRadioGroupInput";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import {
  memberCitizenshipStatusFormSchema,
  MemberCitizenshipStatusFormSchemaType,
} from "@/domain/applications";
import {
  householdMemberCitizenshipStatus,
  HouseholdMemberType,
} from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import { deriveCitizenshipStatusAnswer } from "@/utils/applications";
import {
  formatHouseholdMemberName,
  getAllHouseholdMembersNames,
} from "@/utils/householdMember";

type MemberCitizenshipStatusFormPropsType = {
  member: HouseholdMemberType;
};

const MemberCitizenshipStatusForm: React.FC<
  MemberCitizenshipStatusFormPropsType
> = ({ member }) => {
  const citizenshipStatus = member.citizenshipStatus ?? null;

  const form = useForm<MemberCitizenshipStatusFormSchemaType>({
    defaultValues: {
      citizenshipStatus,
    },
    resolver: standardSchemaResolver(memberCitizenshipStatusFormSchema),
  });

  const { mutateAsync: updateHouseholdMember } =
    useUpdateHouseholdMemberMutation();

  // Auto-save when citizenship status changes
  useEffect(() => {
    const subscription = form.watch((formData, { name }) => {
      if (name === "citizenshipStatus" && formData.citizenshipStatus) {
        void updateHouseholdMember({
          id: member.id,
          householdMemberData: {
            citizenshipStatus: formData.citizenshipStatus,
          },
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [form, member.id, updateHouseholdMember]);

  return (
    <VnForm form={form} isInputOnlyForm>
      <VnCard className="rounded-md p-4">
        <VnCardTitle variant="lgMedium">
          {formatHouseholdMemberName(member)}
        </VnCardTitle>

        <VnRadioGroupInput
          name="citizenshipStatus"
          control={form.control}
          options={householdMemberCitizenshipStatus}
          radioGroupClassName="gap-4 bg-background p-4 rounded-md"
          radioItemClassName="bg-card"
        />
      </VnCard>
    </VnForm>
  );
};

export const ApplicationCitizenshipStatusScreen = () => {
  const { t } = useTranslator();
  const router = useRouter();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);
  const { mutate: updateCurrentPatient } = useUpdateCurrentPatientMutation();

  const { data: householdMembers } = useListHouseholdMembersQuery();

  const { mutateAsync: updateHouseholdMember } =
    useUpdateHouseholdMemberMutation();

  // Track the patient's in-session Yes/No choice; fall back to the answer
  // derived from saved data.
  const [userAnswer, setUserAnswer] = useState<"yes" | "no" | null>(null);
  const derivedAnswer = useMemo(
    () => deriveCitizenshipStatusAnswer(householdMembers ?? []),
    [householdMembers],
  );
  const answer = userAnswer ?? derivedAnswer;
  const isSomeoneNotCitizen = answer === "no";

  const [isYesButtonLoading, setIsYesButtonLoading] = useState(false);

  const isNextDisabled = useMemo(() => {
    if (!householdMembers) return true;
    return householdMembers.some(member => !member.citizenshipStatus);
  }, [householdMembers]);

  const goToNextStep = () => {
    updateCurrentPatient(
      {
        patientData: {
          applicationLastStep: "CITIZENSHIP_STATUS",
        },
      },
      {
        onSuccess: () => {
          setApplicationCurrentStep(step =>
            Math.min(step + 1, applicationTotalSteps),
          );

          router.navigate({
            pathname: "/(authed)/(modal)/application/coverage-status",
          });
        },
      },
    );
  };

  const handleNext = () => {
    if (isNextDisabled) {
      return;
    }

    goToNextStep();
  };

  const onYesPress = async () => {
    if (!householdMembers) {
      return;
    }

    setUserAnswer("yes");

    setIsYesButtonLoading(true);

    const updatePromises = householdMembers.map(member => {
      return updateHouseholdMember({
        id: member.id,
        householdMemberData: {
          citizenshipStatus: citizenshipStatusEnumSchema.enum.US_CITIZEN,
        },
      });
    });

    const results = await Promise.allSettled(updatePromises);

    const failedCount = results.filter(
      result => result.status === "rejected",
    ).length;
    const hasAllFailed = failedCount === results.length;

    if (hasAllFailed) {
      toast.error(
        t("native:eligibilitySpecialConditionsScreen.errors.allUpdatesFailed"),
      );
      setIsYesButtonLoading(false);
      return;
    }

    if (failedCount > 0) {
      toast.error(t("native:someHouseholdMembersNotUpdated"));
    }

    setIsYesButtonLoading(false);

    goToNextStep();
  };

  const onNoPress = () => {
    setUserAnswer("no");
  };

  const householdMembersNames = getAllHouseholdMembersNames(
    householdMembers,
    "firstNameOnly",
  );

  return (
    <ApplicationScreenLayout
      onNextButtonPress={handleNext}
      isNextButtonDisabled={isNextDisabled}
      shouldShowBackButton={false}
      titleI18nKey="native:applicationCitizenshipStatusScreen.title"
      descriptionI18nKey="native:applicationCitizenshipStatusScreen.description"
      descriptionI18nOptions={{
        householdMembersNames: householdMembersNames.join(", "),
      }}>
      <InfoCard titleI18nKey="native:applicationCitizenshipStatusScreen.hintDescription" />

      <VnBox className="gap-3">
        <ApplicationYesNoButton
          onPress={() => {
            void onYesPress();
          }}
          titleI18nKey="common:yes"
          isLoading={isYesButtonLoading}
          descriptionI18nKey="native:applicationCitizenshipStatusScreen.yesDescription"
          isSelected={answer === "yes"}
        />

        <ApplicationYesNoButton
          onPress={onNoPress}
          titleI18nKey="common:no"
          descriptionI18nKey="native:applicationCitizenshipStatusScreen.noDescription"
          isSelected={answer === "no"}
        />
      </VnBox>

      {isSomeoneNotCitizen && (
        <VnBox className="gap-4">
          {householdMembers?.map(member => {
            return (
              <MemberCitizenshipStatusForm key={member.id} member={member} />
            );
          })}
        </VnBox>
      )}
    </ApplicationScreenLayout>
  );
};
